"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const timelineEvents = [{
  id: 1,
  date: "October 2018",
  title: "The First Glance",
  caption: "A chance meeting at a local coffee shop sparked a connection that would last a lifetime.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/first_meeting_coffee.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/first_meeting_coffee.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/first_meeting_coffee.png-wvc-sizes",
    alt: "First meeting at a coffee shop"
  }
}, {
  id: 2,
  date: "December 2022",
  title: "The Yes",
  caption: "Under a canopy of winter stars, a simple question was met with a joyous answer.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/winter_proposal_stars.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/winter_proposal_stars.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/winter_proposal_stars.png-wvc-sizes",
    alt: "Winter proposal under the stars"
  }
}, {
  id: 3,
  date: "June 2025",
  title: "The I Do",
  caption: "Join us as we begin our next chapter surrounded by the people we love most.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_rings_champagne.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_rings_champagne.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_rings_champagne.png-wvc-sizes",
    alt: "Wedding rings and champagne"
  }
}];
export default function OurPathToIDo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  return <section id="our-path-to-i-do" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Our Path to I Do
          </h2>
          <p className="text-muted-foreground text-lg font-sans leading-relaxed">
            Every great love story is a journey. Here are a few of the beautiful moments that led us to this day.
          </p>
        </div>

        <div ref={ref} className="relative w-full overflow-x-auto hide-scrollbar pb-12 -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-6 md:gap-8 min-w-max md:min-w-0 md:w-full md:justify-between relative pt-4">
            
            <div className="absolute top-[140px] left-0 h-[1px] bg-border w-full -z-10 hidden md:block" />
            
            <div className={cn("absolute top-[140px] left-0 h-[1px] bg-primary -z-10 hidden md:block origin-left transition-transform duration-[1500ms] ease-in-out", isVisible ? "scale-x-100" : "scale-x-0")} />

            {timelineEvents.map((event, i) => <div key={event.id} data-index={i} className="flex flex-col items-center shrink-0 group !w-[390px]">
                <div className="w-80 h-48 mb-8 overflow-hidden rounded-lg shadow-md relative">
                  <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
                  <img src={event.image.src} data-wvc-srcset={event.image.srcset} data-wvc-sizes={event.image.sizes} alt={event.image.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>

                <div className="relative flex items-center justify-center w-full mb-8 md:mb-10">
                  <div className="absolute top-1/2 left-0 h-[1px] bg-border w-full -z-10 md:hidden" />
                  <div className={cn("absolute top-1/2 left-0 h-[1px] bg-primary -z-10 md:hidden origin-left transition-transform duration-[1500ms] ease-in-out", isVisible ? "scale-x-100" : "scale-x-0")} />
                  
                  <div className="size-3 bg-background border border-primary rotate-45 rounded-sm shadow-sm flex items-center justify-center z-10 transition-colors duration-500 group-hover:bg-primary">
                    <div className="size-1 bg-primary transition-colors duration-500 group-hover:bg-background" />
                  </div>
                </div>

                <div className="text-center px-4">
                  <span className="block font-serif text-primary text-lg mb-2 italic">
                    {event.date}
                  </span>
                  <h3 className="font-default text-xl font-semibold text-foreground mb-3">
                    {event.title}
                  </h3>
                  <p className="text-muted-foreground text-sm font-sans leading-relaxed">
                    {event.caption}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="mt-16 md:mt-24 flex justify-center">
          <Button asChild variant="outline" size="lg" className="rounded-md px-8 font-default tracking-wide">
            <Link to="/OurStoryPage">
              Read Our Full Story
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}